import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import store from '../state/store.js'
import routes from './routes.js'
import { SSO_ERROR } from './route-names.js'
import { NO_ACCESS } from '../../../shared/router/route-names.js'
import { waitForNavBarItemsLoad, navBarItems } from '../../../shared/state/admin-portal-navigation.js'
import { waitForLoad, privacyNoticeModuleEnabled } from '../../../shared/state/config-keys.js'
import { getLandingPageInfo } from '../../../shared/utils/api/landing-page.js'
import { hasMultipleProductAccess } from '../../../shared/utils/product-access.js'
import { getEnv } from '../../../shared/utils/runtime-settings.js'
import { uuid } from '../../../shared/utils/uuid.js'
import { navigateToDashboardPortal } from '../../../shared/utils/navigation/dashboard-navigation.js'
import { navigateToPrefPageAnalytics } from '../../../shared/utils/navigation/prefpage-analytics-navigation.js'

const CONSENT_BANNERS_DASHBOARD_ID = 0
const PREF_PAGE_ANALYTICS_DASHBOARD_ID = -1

// Silencing push/replace errors (NavigationDuplicated)
// Required after upgrading to v3.1
// https://github.com/vuejs/vue-router/issues/2881
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (location, onResolve, onReject) {
	if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
	return originalPush.call(this, location).catch(err => err)
}
Vue.use(VueRouter)
Vue.use(VueMeta, {
	// The component option name that vue-meta looks for meta info on.
	keyName: 'page'
})

const router = new VueRouter({
	routes,
	// Use the HTML5 history API (i.e. normal-looking routes)
	// instead of routes with hashes (e.g. example.com/#/about).
	// This may require some server configuration in production:
	// https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
	mode: 'history',
	// Simulate native-like scroll behavior when navigating to a new
	// route and using back/forward buttons.
	scrollBehavior (to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition
		} else {
			return { x: 0, y: 0 }
		}
	},
	base: process.env.BASE_URL
})
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
	// === Exchange Token Redemption (Risk Portal -> Admin Portal SSO) ===
	// Allow multiple legitimate redemptions in the same tab (new tokens) while preventing duplicate processing
	// of the same token. We store a SHA-256 hash (or fallback identifier) of redeemed tokens in sessionStorage.
	// Key: 'redeemedExchangeTokens' -> JSON array of token hashes. Single-use tokens will naturally fail on server if reused.
	try {
		const search = window.location.search
		if (search.includes('drkpnk=')) {
			const params = new URLSearchParams(search)
			const exchangeToken = params.get('drkpnk')
			if (exchangeToken) {
				// Load previously redeemed token hashes
				let redeemedList = []
				try {
					const raw = sessionStorage.getItem('redeemedExchangeTokens')
					if (raw) redeemedList = JSON.parse(raw)
				} catch {}

				// Hash token for storage (avoid keeping raw token). Fallback: simple prefix + length if crypto not available.
				async function hashToken (token) {
					try {
						if (window.crypto?.subtle && typeof TextEncoder !== 'undefined') {
							const data = new TextEncoder().encode(token)
							const digest = await window.crypto.subtle.digest('SHA-256', data)
							const bytes = Array.from(new Uint8Array(digest))
							return bytes.map(b => b.toString(16).padStart(2, '0')).join('')
						}
					} catch {}
					return 'tk_' + token.length + '_' + token.slice(0, 6)
				}

				const tokenHash = await hashToken(exchangeToken)
				const alreadyRedeemed = redeemedList.includes(tokenHash)
				if (!alreadyRedeemed) {
					// Dynamic import keeps bundle lean
					const { redeemExchangeToken } = await import('../../shared/utils/api/exchange-token.js')
					const result = await redeemExchangeToken(exchangeToken)

					// Record this token hash regardless of success to prevent pointless repeats with identical token
					redeemedList.push(tokenHash)
					// Keep list size reasonable (last 20)
					if (redeemedList.length > 20) redeemedList = redeemedList.slice(-20)
					try { sessionStorage.setItem('redeemedExchangeTokens', JSON.stringify(redeemedList)) } catch {}

					if (result.success) {
						await store.dispatch('auth/setJwtTokenFromString', result.token)
						await store.dispatch('auth/getUserDetails')
						// Clean URL (remove sensitive exchange token while preserving any other query params)
						const currentPath = window.location.pathname || '/'
						try {
							const currentSearch = window.location.search || ''
							const searchParams = new URLSearchParams(currentSearch)
							searchParams.delete('drkpnk')
							const remainingQuery = searchParams.toString()
							const cleanUrl = currentPath + (remainingQuery ? `?${remainingQuery}` : '') + window.location.hash
							window.history.replaceState(null, '', cleanUrl)
						} catch {
							// Fallback: if URLSearchParams or replaceState fails, at least drop the query entirely
							const cleanUrl = currentPath + window.location.hash
							window.history.replaceState(null, '', cleanUrl)
						}
						// Mark that we've already shown the splash when arriving from
						// an external exchange-token flow, so deep links don't bounce.
						try { sessionStorage.setItem('saw-splash', 'true') } catch {}
						// If we're on legacy callback paths (e.g. /auth or root),
						// fall back to the default landing route. Otherwise, allow
						// navigation to continue to the requested path (e.g.
						// /cookie-categories provided directly by the caller).
						if (currentPath === '/auth' || currentPath === '/' || currentPath === '/login') {
							try {
								const { getDefaultAdminLandingRoute } = await import('../utils/postLoginDestination.js')
								return next({ path: getDefaultAdminLandingRoute() })
							} catch { return next({ path: '/dashboard' }) }
						}
						return next({ path: currentPath, replace: true })
					} else {
						return next({ path: '/login' })
					}
				} // else identical token already processed; fall through to normal guard behavior.
			}
		}
	} catch (ex) {
		console.warn('[exchange] redemption exception', ex)
	}
	// If this isn't an initial page load...
	if (routeFrom.name !== null) {
		// Start the route progress bar.
		NProgress.start()
	}

	// Allow navigation to NO_ACCESS page without checks
	if (routeTo.name === NO_ACCESS) {
		return next()
	}

	// Check for cookie indicating existing sesion
	await store.dispatch('auth/checkForExistingSession')
	// Check query string for JWT	token
	const urlParams = new URLSearchParams(window.location.search)
	if (urlParams.has('migjwt') && document.referrer !== null && document.referrer !== '') {
		const jwtString = urlParams.get('migjwt')
		sessionStorage.setItem('migjwt', 'true')
		await store.dispatch('auth/setJwtTokenFromString', jwtString)
		await store.dispatch('auth/getLegacyUrl', jwtString)
	}

	let userIsValid = false
	let shouldContinue = true
	// Check if user is logged in but we haven't got their permission details
	if (store.getters['auth/loggedIn'] && !store.getters['auth/userPermissionsRetrieved']) {
		// Check if we already detected insufficient access
		if (store.getters['auth/hasInsufficientAccess']) {
			return next({ name: NO_ACCESS })
		}
		await store.dispatch('auth/validate').then(validUser => {
			userIsValid = (!!validUser)
			if (!userIsValid) {
				shouldContinue = false
				next({ path: '/login' })
			}
		})
		if (userIsValid && shouldContinue) {
			await store.dispatch('auth/getUserDetails').catch(error => {
				console.warn(error)
				shouldContinue = false
				// Check if it's an insufficient access error
				if (error.message === 'INSUFFICIENT_ACCESS') {
					next({ name: NO_ACCESS })
					return
				}
				next({ path: '/login' })
			})
		}

		if (!shouldContinue) {
			return
		}
	}
	// Additional check in case flag was set
	if (store.getters['auth/hasInsufficientAccess']) {
		return next({ name: NO_ACCESS })
	}
	if (store.getters['auth/loggedIn']) {
		await waitForNavBarItemsLoad()
		await waitForLoad()
	}
	// Redirect if navigating to the login page while already logged in (ADO#9680)
	let redirectPath = null
	if (routeTo != null && routeTo.name === 'login' && store.getters['auth/loggedIn'] && userIsValid) {
		// If there is no 'routeFrom' available, pick the first valid navbar route to redirect to
		let firstAvailableNavItem = null
		if (routeFrom?.name == null) {
			firstAvailableNavItem = navBarItems.value.find(topLevelNavItem => !topLevelNavItem.isExternal && topLevelNavItem.route.name != null && topLevelNavItem.sublinks.length < 1)
			if (firstAvailableNavItem == null) {
				for (let i = 0; i < navBarItems.value.length; i++) {
					if (firstAvailableNavItem != null) { break }
					firstAvailableNavItem = navBarItems.value[i].sublinks.find(sublink => !sublink.isExternal && sublink.route.name != null && sublink.deeperSublinks.length < 1)
					if (firstAvailableNavItem == null) {
						for (let j = 0; j < navBarItems.value[i].sublinks.length; j++) {
							if (firstAvailableNavItem != null) { break }
							firstAvailableNavItem = navBarItems.value[i].sublinks[j].deeperSublinks.find(deeperSublink => !deeperSublink.isExternal && deeperSublink.route != null && deeperSublink.route !== '')
						}
					}
				}
			}
		}

		redirectPath = routeFrom.path ?? firstAvailableNavItem.route?.name

		if (redirectPath == null) {
			// If all else fails
			redirectPath = '/login'
		}
		router.push({ path: redirectPath })
	}

	const isLogin = routeTo.name === 'login'
	const authRequired = routeTo.meta?.authRequired
	const permissions = routeTo.meta?.permissionsRequired

	let isSSOLoginType = false
	let identityClaimTypeName = null
	let tenantGuid = null
	if (isLogin) {
		const response = await getLandingPageInfo(1)
		const loginConfig = response.data?.loginConfig
		isSSOLoginType = loginConfig?.loginType === 'SSO' && !urlParams.has('noredirect')
		identityClaimTypeName = loginConfig?.identityClaimTypeName
		tenantGuid = loginConfig?.tenantGuid
	}
	const userLoggedIn = store.getters['auth/loggedIn']
	if (userLoggedIn) { // user already logged in
		// Check if user has access to multiple products (Admin Portal + Risk Portal and/or Privacy Notices)
		const productAreaPermission = store.getters['auth/productAreaPermission']
		const hasMultipleProducts = hasMultipleProductAccess(productAreaPermission, privacyNoticeModuleEnabled.value)

		// Only redirect to splash if user has access to multiple products
		// Single-product users (admin-only, risk-only, privacy-notices-only) skip splash
		if (hasMultipleProducts && routeTo.name !== 'splash' && routeTo.name !== 'logout' && routeTo.name !== 'SSO_ERROR') {
			if (!sessionStorage.getItem('saw-splash')) {
				sessionStorage.setItem('saw-splash', 'true')
				return next({ name: 'splash' })
			}
		}

		// Redirect users with an external default dashboard on initial login
		const userDefaultDashboardId = store.getters['auth/userDefaultDashboardId']
		const isInitialDashboardNav = routeTo.name === 'dashboard' || routeTo.path === '/dashboard' || routeTo.path === '/'

		if (
			userDefaultDashboardId === CONSENT_BANNERS_DASHBOARD_ID &&
			isInitialDashboardNav &&
			!sessionStorage.getItem('consent-banners-redirect-checked')
		) {
			try {
				const redirected = await navigateToDashboardPortal()
				if (redirected) {
					sessionStorage.setItem('consent-banners-redirect-checked', 'true')
					return // Stop navigation - we're redirecting externally
				}
				// Redirect URL couldn't be generated — fall through to dashboard
			} catch (err) {
				console.warn('[consent-banners] redirect failed, continuing to dashboard:', err)
			}
			sessionStorage.setItem('consent-banners-redirect-checked', 'true')
		}

		if (
			userDefaultDashboardId === PREF_PAGE_ANALYTICS_DASHBOARD_ID &&
			isInitialDashboardNav &&
			!sessionStorage.getItem('pref-page-analytics-redirect-checked')
		) {
			try {
				const redirected = await navigateToPrefPageAnalytics()
				if (redirected) {
					sessionStorage.setItem('pref-page-analytics-redirect-checked', 'true')
					return
				}
			} catch (err) {
				console.warn('[pref-page-analytics] redirect failed, continuing to dashboard:', err)
			}
			sessionStorage.setItem('pref-page-analytics-redirect-checked', 'true')
		}

		return next()
	}
	if (authRequired || isSSOLoginType) {
		if (isSSOLoginType) {
			// SSO Login
			const authHandler = getEnv('VUE_APP_BASE_URL_AUTH')

			sessionStorage.setItem('tenantGuid', tenantGuid)
			sessionStorage.setItem('authHandler', authHandler)
			sessionStorage.setItem('isSSO', 'true')

			const ssoState = sessionStorage.getItem('sso-state-admin')
			if (ssoState) { // existing SSO attempt in progress, so retrieve token
				const { identityClaimTypeName, stateGuid, productId } = JSON.parse(ssoState)
				sessionStorage.removeItem('sso-state-admin')
				try {
					// invoke /api/Authorize/SSO to get the JWT token
					await store.dispatch('auth/ssoLogIn', { identityClaimTypeName, stateGuid, productId })
					next({ path: '/dashboard' })
				} catch (_) {
					router.push({ name: SSO_ERROR })
				}
			} else { // initiate new SSO attempt
				const stateGuid = uuid()
				sessionStorage.setItem('sso-state-admin', JSON.stringify({ identityClaimTypeName, stateGuid, productId: 1 }))
				window.location.href = `${authHandler}/${tenantGuid}/${stateGuid}`
			}
			// if no migration URL & not logged in, redirect to login
		} else if (store.getters['auth/migrationUrl'] == null && !store.getters['auth/loggedIn']) {
			// if route is site-scanner-resultor a site-scanner-download route, redirect to login with previousRoute param
			routeTo.name.includes('site-scanner-result') || routeTo.name.includes('site-scanner-download') ? router.push({ name: 'login', params: { previousRoute: routeTo } }) : next({ path: '/login' })
		} else if (permissions !== undefined && !store.getters['auth/productAreaPermission'](permissions)) {
			// If user tries to go to route they don't have permission for, redirect to the first route they have access to, or forbidden page if none available
			const redirectOr403 = redirectPath !== '/login' && redirectPath != null ? redirectPath : '/403'
			next({ path: redirectOr403 })
		} else {
			await store.dispatch('auth/validate').then(validUser => {
				// Then continue if the token still represents a valid user,
				// otherwise redirect to login.
				validUser ? next() : next({ path: '/login' })
			})
		}
	} else if (routeTo.path.search('http') >= 0) {
		window.open(routeTo.path, '_blank', 'noreferrer')
	} else {
		next()
	}
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
	// Complete the animation of the route progress bar.
	NProgress.done()
})
export default router
